import { ValidateResult } from 'react-hook-form';
import { AllowedExtensionsType } from './AppAttachmentsV2.d';
import { AppAttachmentValue } from './AppAttachmentsV2.d';
import { AppAttachmentValueWithErrors } from './AppAttachmentsV2.d';
import { getFileExtension } from 'src/helpers/file-helper';
import _ from 'lodash';
import i18next from 'i18next';

export const validateFileRule =
  (allowedFileTypes: AllowedExtensionsType[] | undefined, maxFileSize: number | undefined) =>
  (value: AppAttachmentValue[] | undefined): ValidateResult => {
    const errors = value?.reduce<Record<string, string[]>>((result, file) => {
      const fileSizeValid = !maxFileSize || (file.fileSizeBytes ?? 0) <= maxFileSize * 1024 * 1024;
      const fileTypeValid =
        allowedFileTypes && (allowedFileTypes as string[]).includes(getFileExtension(file.fileName).toLowerCase());

      if (!fileSizeValid)
        result = {
          ...result,
          [file.fileName]: [...(result.fileName ?? []), i18next.t('uikit.Attachments.Validation.BigFileSize')],
        };
      if (!fileTypeValid)
        result = {
          ...result,
          [file.fileName]: [...(result.fileName ?? []), i18next.t('uikit.Attachments.Validation.NotAllowedFileType')],
        };
      return result;
    }, {});

    return _.isEmpty(errors) ? undefined : JSON.stringify(errors);
  };

export const mergeValueWithError = (
  value: AppAttachmentValue[] | undefined,
  error: Record<string, string[]> | undefined,
): AppAttachmentValueWithErrors[] | undefined => {
  if (!value) return value;

  if (error)
    return value.map(
      (v) =>
        ({
          ...v,
          errors: error ? error[v.fileName] : undefined,
        } satisfies AppAttachmentValueWithErrors),
    );

  return value;
};

/**
 * Parses an error input into a structured object.
 *
 * @param {string | string[] | undefined} input - Raw error string or array of strings.
 * @returns {{ generalError?: string, fileErrors?: Record<string, string[]> }}
 */
export const parseError = (
  input: string | string[] | undefined,
): { generalError?: string; fileErrors?: Record<string, string[]> } => {
  const result: {
    generalError: string | undefined;
    fileErrors: Record<string, string[]> | undefined;
  } = {
    generalError: undefined,
    fileErrors: undefined,
  };

  const target = _.isArray(input) ? _.head(input) : input;
  if (!_.isString(target) || _.isEmpty(target)) {
    return result;
  }

  try {
    const parsed = JSON.parse(target);
    if (_.isPlainObject(parsed)) {
      result.fileErrors = parsed;
    } else {
      result.generalError = target;
    }
  } catch (e) {
    result.generalError = target;
  }

  return result;
};
